/* eslint-disable prettier/prettier */
import React from 'react';
import { Box, Text, Spinner, Flex } from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useTaxAnalysis } from '../../../../../hooks/useTaxAnalysis';
import { formatarEmReais, ITaxAnalysisHistory } from '../../../../../types/taxAnalysis';

interface TaxFaturamentoChartProps {
  companyId: string | number | undefined;
  dateStart?: string;
  dateEnd?: string;
}

const FaturamentoTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        bg="rgba(255, 255, 255, 0.85)"
        backdropFilter="blur(12px)"
        p={4}
        borderRadius="12px"
        boxShadow="0 4px 24px rgba(0, 0, 0, 0.08)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.3)"
        minW="160px"
      >
        <Text fontWeight="700" mb={2} color="gray.800" fontSize="sm">
          {label}
        </Text>
        <Flex align="center" gap={2}>
          <Box w={3} h={3} borderRadius="full" bg="#0A84FF" />
          <Text fontSize="sm" color="gray.600">
            Faturamento:
          </Text>
        </Flex>
        <Text fontSize="lg" fontWeight="600" color="gray.800" mt={1}>
          {formatarEmReais(payload[0].value)}
        </Text>
      </Box>
    );
  }
  return null;
};

export function TaxFaturamentoChart({ companyId, dateStart, dateEnd }: TaxFaturamentoChartProps) {
  const { getTaxAnalysisHistoryByPeriod } = useTaxAnalysis();

  // Query para buscar histórico de faturamento
  const { data: historyData, isFetching: isLoading } = useQuery<ITaxAnalysisHistory[]>({
    queryKey: ['TaxFaturamentoHistory', { companyId, dateStart, dateEnd }],
    queryFn: getTaxAnalysisHistoryByPeriod,
    enabled: !!companyId && !!dateStart && !!dateEnd,
  });

  // Formatar dados para o gráfico
  const formattedData = (historyData || []).map(item => ({
    periodo: item.mes,
    faturamento: item.total_faturamento,
  }));

  // Determinar o título baseado no período
  const getChartTitle = () => {
    if (!dateStart || !dateEnd) return 'Evolução do Faturamento';
    const start = moment(dateStart);
    const end = moment(dateEnd);
    const diffMonths = end.diff(start, 'months');
    return `Evolução do Faturamento (${diffMonths} meses)`;
  };

  return (
    <Box
      p={6}
      bg="linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
      borderRadius="16px"
      boxShadow="0 4px 24px rgba(0, 0, 0, 0.06)"
      border="1px solid rgba(0, 0, 0, 0.04)"
    >
      <Text fontSize="md" fontWeight="600" mb={4} color="gray.800">
        {getChartTitle()}
      </Text>
      {isLoading && (
        <Flex justify="center" align="center" h="300px">
          <Spinner size="lg" color="blue.500" />
        </Flex>
      )}
      {!isLoading && formattedData.length > 0 && (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="colorFaturamento" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0A84FF" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#0A84FF" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="periodo" tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis
              tickFormatter={value => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                return value.toString();
              }}
              tick={{ fontSize: 12, fill: '#6B7280' }}
            />
            <Tooltip content={<FaturamentoTooltip />} />
            <Area
              type="monotone"
              dataKey="faturamento"
              name="Faturamento"
              stroke="#0A84FF"
              strokeWidth={2.5}
              fill="url(#colorFaturamento)"
              dot={{ fill: '#fff', strokeWidth: 2, r: 4, stroke: '#0A84FF' }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: '#0A84FF', fill: '#fff' }}
              animationBegin={0}
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
      {!isLoading && formattedData.length === 0 && (
        <Flex justify="center" align="center" h="300px">
          <Text color="gray.500">Sem dados de faturamento disponiveis</Text>
        </Flex>
      )}
    </Box>
  );
}
